import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CaseNoteType } from '../api/CaseNote';
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { isReportsPolicyNormalizationEnabled } from "../features/configs/logic/Slice";
import { selectPartiesWithRecommendedAction } from "../features/partiesinvolved/logic/PartiesInvolvedSelectors";
import { selectCurrentRecommendation } from "../features/recommendations/logic/Slice";
import { selectPolicyImprovements, selectPolicyImprovementsStatus } from "../features/recommendations/logic/PolicyImprovementsSlice";
import { fetchPolicyImprovementsThunk } from "../features/recommendations/logic/PolicyImprovementsThunk";
import { useCaseInfoData } from '../hooks/useCaseInfoData';
import { useInterviewFindings } from '../hooks/useCaseInterviews';
import { useCaseObservations } from '../hooks/useObservations';
import { useRecommendations } from '../hooks/useRecommendations';
import { useCaseNotes } from './useCaseNotes';
import { useCaseOverview } from './useCaseOverview';


export const useInvestigationReport = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
    
  const caseInfoData = useCaseInfoData();
  const caseObservationsData = useCaseObservations();
  const caseOverviewData = useCaseOverview();
  const caseRecommendation = useRecommendations();
  const caseInterviewFindingsData = useInterviewFindings();

  const isPolicyImprovementV2Enabled = useAppSelector(isReportsPolicyNormalizationEnabled);
  const policyImprovementsV2 = useAppSelector(selectPolicyImprovements);
  const policyV2Status = useAppSelector(selectPolicyImprovementsStatus);

  useEffect(() => {
    if (caseId && isPolicyImprovementV2Enabled) {
      dispatch(fetchPolicyImprovementsThunk({ caseId }));
    }
  }, [caseId, isPolicyImprovementV2Enabled, dispatch]);

  // Remarks
  const { initialValue: caseOverviewRemarks } = useCaseNotes({ noteType: CaseNoteType.case_details})
  const { initialValue: caseCollateralRemarks } = useCaseNotes({ noteType: CaseNoteType.collateral_verification})
  const { initialValue: caseInterviewFindingsRemarks } = useCaseNotes({ noteType: CaseNoteType.interview_findings})
  const { initialValue: casePortfolioAnalysisRemarks } = useCaseNotes({ noteType: CaseNoteType.portfolio_analysis})
  const { initialValue: caseBorrowerRemarks } = useCaseNotes({ noteType: CaseNoteType.profile_verification})

  const isLoading = 
    caseInfoData.isLoading ||
    caseObservationsData.isLoading ||
    caseOverviewData.isLoading ||
    caseRecommendation.isLoading ||
    (isPolicyImprovementV2Enabled && (policyV2Status === 'idle' || policyV2Status === 'loading'))
  ;
  
  const caseRecommendationsData = useAppSelector((state) => 
    selectCurrentRecommendation(state, caseId)
  );

  const partiesWithRecommendedAction = useAppSelector(
    selectPartiesWithRecommendedAction
  );

  return {
    caseInfoData,
    caseObservationsData,
    caseOverviewData,
    caseRecommendationsData,
    partiesWithRecommendedAction,
    caseInterviewFindingsData,
    isPolicyImprovementV2Enabled,
    policyImprovementsV2,
    remarks: {
      'los-lms-remarks': caseOverviewRemarks,
      'interview-findings-remarks': caseInterviewFindingsRemarks,
      'portfolio-analysis-remarks': casePortfolioAnalysisRemarks,
      'borrower-verification-remarks': caseBorrowerRemarks,
      'collateral-verification-remarks': caseCollateralRemarks,
    },
    isLoading,
  }
};